import type { FC, ReactNode } from 'react'

import { useEffect, useRef } from 'react'
import { subscribeFrame } from '@/lib/universe/store'
import { Media } from '@/components/generals/media'

interface ObjectStillProps {
    src: string
    alt: string
}

// Most tilt, in degrees, as the cursor crosses the window. The object never spins.
const TILT_X = 7
const TILT_Y = 9

// An object photographed on black. It is screened onto the page, so its black falls away
// and only its light remains over the hole. It leans slowly towards the cursor, and a soft
// highlight slides across the glass and chrome as it does.
export const ObjectStill: FC<ObjectStillProps> = ({ src, alt }): ReactNode => {
    const tiltRef = useRef<HTMLDivElement>(null)
    const sheenRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        const target = { x: 0, y: 0 }
        const current = { x: 0, y: 0 }
        const fine = window.matchMedia('(hover: hover) and (pointer: fine)').matches

        const onMove = (event: PointerEvent): void => {
            if (event.pointerType === 'touch') return

            target.x = (event.clientX / window.innerWidth) * 2 - 1
            target.y = (event.clientY / window.innerHeight) * 2 - 1
        }

        if (fine) window.addEventListener('pointermove', onMove, { passive: true })

        const stop = subscribeFrame((frame, dt) => {
            const tilt = tiltRef.current
            const sheen = sheenRef.current

            if (!tilt || !sheen) return

            const follow = 1 - Math.exp(-dt * 2.2)
            const goalX = frame.reducedMotion ? 0 : target.x
            const goalY = frame.reducedMotion ? 0 : target.y

            current.x += (goalX - current.x) * follow
            current.y += (goalY - current.y) * follow

            tilt.style.transform = `perspective(1600px) rotateX(${(8 - current.y * TILT_X).toFixed(3)}deg) rotateY(${(current.x * TILT_Y).toFixed(3)}deg) rotateZ(-5deg)`
            sheen.style.backgroundPosition = `${(50 - current.x * 22).toFixed(2)}% ${(42 - current.y * 22).toFixed(2)}%`
        })

        return () => {
            stop()
            window.removeEventListener('pointermove', onMove)
        }
    }, [])

    return (
        <div className='relative aspect-square w-full'>
            <div
                ref={tiltRef}
                style={{ transform: 'perspective(1600px) rotateX(8deg) rotateZ(-5deg)' }}
                className='umbra-object absolute inset-0 will-change-transform'
            >
                <Media src={src} alt={alt} sizes='(min-width: 768px) 38vw, 86vw' preload />

                <div
                    ref={sheenRef}
                    aria-hidden='true'
                    className='umbra-sheen absolute inset-0'
                />
            </div>
        </div>
    )
}
